<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <!-- Dynamic Parent -->
      <router-link v-if="fromNotifications"
        :to="{ name: 'ViewPendingNotifications', query: { entityType: route.query.entityType, statusInd: route.query.statusInd } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Notifications
      </router-link>

      <router-link v-else :to="{ name: 'ViewAppraisals' }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Appraisals
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Update Appraisal</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer">
        <form @submit.prevent="step === totalSteps ? handleSubmit() : handleNext()">
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <!-- <h4>Update Appraisal (Step {{ step }} of {{ totalSteps }})</h4> -->
                <h1 class="mb-0">Appraisal</h1>
                <button type="button" class="btn btn-primary btn-sm view-memo-btn" @click="openMemoPreview"
                  :disabled="!form.memoFileNumber" title="View Memo Details">
                  <span class="material-symbols-outlined align-middle me-1" style="font-size: 1.1rem;">description</span>
                  View Memo
                </button>
              </div>

              <div class="header-box d-flex justify-content-between align-items-center mb-3">
                <div class="progress-bar" :style="{
                  '--progress-width': progressWidth,
                  '--step-count': totalSteps
                }">
                  <!-- Row 1: Step Icons -->
                  <div class="step-icon" v-for="(s, i) in totalSteps" :key="'icon-' + i">
                    <div :class="['step', { active: step === i + 1, completed: step > i + 1 }]">
                      {{ step > i + 1 ? '✓' : i + 1 }}
                    </div>
                  </div>

                  <!-- Row 2: Progress Line -->
                  <div class="progress-line" :style="{ '--progress-width': progressWidth }" />

                  <!-- Row 3: Step Labels -->
                  <div class="step-label text-wrap" v-for="(s, i) in steps" :key="'label-' + i"
                    :class="{ active: step === i + 1 }">
                    {{ s.label }}
                  </div>
                </div>
              </div>

              <div v-if="hasAssociatedPaymentVoucher" class="marquee-wrapper">
                <div class="marquee-content">
                  <p>
                    <span class="material-symbols-outlined">
                      asterisk
                    </span> This appraisal has an associated Payment Voucher
                  </p>
                  <p>
                    <span class="material-symbols-outlined">
                      asterisk
                    </span>This appraisal has an associated Payment Voucher
                  </p>
                  <p>
                    <span class="material-symbols-outlined">
                      asterisk
                    </span>This appraisal has an associated Payment Voucher
                  </p>
                </div>
              </div>

              <div v-if="step === 1">
                <div class="form-body">
                  <!-- <h2 class="field-head">A. General Information</h2> -->
                  <div class="row mb-3">
                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Appraisal Initiated Date</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input "
                          :value="formatInitDate(form.appraisalInitiatedDate)" placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Originating MDA</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input " v-model="form.originatingMda"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Approval Authority</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input " v-model="form.approvalAuthority"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Budget Year</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input " v-model="form.budgetYear"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Quarter</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input " :value="'Q' + form.quarter"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Nature of Expenditure</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input " :value="natureOfExpenditureLabel"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Amount Approved</label></div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control form-view-only-input "
                          :value="'₦' + formatCurrency(form.amountApproved || 0).toLocaleString()" placeholder=""
                          readonly>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Purpose of Request</label></div>
                      <div class="col-md-9 input-right">
                        <textarea type="text" class="form-control form-view-only-input"
                          v-model="form.purposeOfRequest" placeholder="" rows="3" readonly>
                        </textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="step === 2">
                <!-- <h2 class="field-head">B. Budget</h2> -->
                <div class="row mb-3 budget-item-wrapper step-2 justify-content-center">
                  <div class="row content-wrapper section-border h-100 w-100">

                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Organizational Code</label>
                        <input type="text" class="form-control view-only-input text-center" v-model="form.orgCode"
                          readonly>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Budget Year</label>
                        <input type="text" class="form-control view-only-input text-center" v-model="form.budgetYear"
                          readonly>
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Approved Budget Amount</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + formatCurrency(form.approvedBudgetAmount || 0).toLocaleString()" readonly />
                      </div>
                    </div>

                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Approved Released To Date</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + formatCurrency(form.amtReleasedToDate || 0).toLocaleString()" readonly />
                      </div>
                    </div>
                    <div class="col d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label">Balance</label>
                        <!-- display the formatted string -->
                        <input type="text" class="form-control view-only-input text-center"
                          :value="'₦' + formatCurrency(form.balance || 0).toLocaleString()" readonly />
                      </div>
                    </div>
                    <div class="col no-border d-flex">
                      <div class="d-flex flex-column justify-content-center h-100 w-100">
                        <label class="form-label fw-bold fixed-label ">Performance (%)</label>
                        <input type="text" class="form-control view-only-input text-center"
                          :value="Number(form.performance || 0).toFixed(2) + '%'" readonly>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="table-responsive mt-4">
                  <table class="table table-bordered text-center align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Eco Code</th>
                        <th>Title</th>
                        <th>Approved Budget {{ form.budgetYear }}</th>
                        <th>Approval Release To Date</th>
                        <th>Balance</th>
                        <!-- <th>Performance (%)</th> -->
                        <th>Amount Required</th>
                        <th>Amount Recommended</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in economicItems" :key="index"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td>
                          {{ item.ecoCode }}
                        </td>
                        <td>
                          {{ item.title }}
                        </td>
                        <td>
                          ₦{{ formatCurrency(item.amountBudgeted || 0).toLocaleString() }}
                        </td>
                        <td>
                          ₦{{ formatCurrency(item.amountReleased || 0).toLocaleString() }}
                        </td>
                        <td>
                          ₦{{ formatCurrency(item.balance || 0).toLocaleString() }}
                        </td>
                        <!-- <td>
                                      {{ item.performance }}%
                                    </td> -->
                        <td>
                          ₦{{ formatCurrency(item.amountRequired || 0).toLocaleString() }}
                        </td>
                        <td>
                          ₦{{ formatCurrency(item.eliAmountRecommended || 0).toLocaleString() }}
                        </td>

                        <!-- <td>
                                      <input
                                        type="number"
                                        min="0"
                                        class="form-control form-control-sm"
                                        v-model.number="item.amountRequired"
                                        @input="validateBudgetStep"
                                      />
                                      {{ item.amountRequired }}
                                    </td> -->
                      </tr>
                    </tbody>
                  </table>

                  <!-- <div class="mt-3 text-end">
                                <button type="button" class="btn btn-outline-primary btn-sm" @click="addEconomicItem">
                                  <span class="iconify" data-icon="mdi:plus"></span> Add More
                                </button>
                              </div> -->
                </div>
              </div>


              <div v-if="step === 3">
                <!-- <h2 class="field-head">C. Evaluation Of Expenditure Approval Limits</h2> -->
                <div class="row mb-3 align-items-center justify-content-center">
                  <div class="row form-group position-relative floating-label-group">
                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label fw-bold">Year:</label></div>
                    <div class="col-md-1 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.budgetYear" placeholder=""
                        readonly>
                    </div>

                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label fw-bold">Date:</label></div>
                    <div class="col-md-2 input-right">
                      <input type="text" class="form-control view-only-input"
                        :value="formatInitDate(form.appraisalInitiatedDate)" placeholder="" readonly>
                    </div>

                    <div class="col-md-2 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label fw-bold">Approval Authority:</label></div>
                    <div class="col-md-3 input-right">
                      <input type="text" class="form-control view-only-input" v-model="form.approvalAuthority"
                        placeholder="" readonly>
                    </div>

                    <div class="col-md-1 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label fw-bold">Quarter:</label></div>
                    <div class="col-md-1 input-right">
                      <input type="text" class="form-control view-only-input" :value="'Q' + form.quarter" placeholder=""
                        readonly>
                    </div>
                  </div>

                  <!-- <div class="row mb-3 budget-item-wrapper justify-content-center" style="margin-left: 3px; margin-right: 3px; margin-top:0;">
                                <div class="col-md-2 d-flex no-border">
                                  <div class="d-flex flex-row justify-content-center h-100 w-100">
                                  <label for="year" class="form-label fw-bold fixed-label">Year:</label>
                                  <input type="text" class="form-control view-only-input text-center" v-model="form.year" placeholder="" readonly>
                                </div>
                                </div>

                                <div class="col-md-3 d-flex no-border">
                                  <div class="d-flex flex-row justify-content-center h-100 w-100">
                                  <label for="year" class="form-label fw-bold fixed-label">Date:</label>
                                    <input type="text" class="form-control view-only-input text-center" v-model="form.exp_date" placeholder="" readonly>
                                  </div>
                                </div>

                                <div class="col-md-2 d-flex no-border">
                                  <div class="d-flex flex-row justify-content-center h-100 w-100">
                                    <label for="year" class="form-label fw-bold fixed-label">Quarter:</label>
                                    <input type="text" class="form-control view-only-input text-center" v-model="form.exp_quarter" placeholder="" readonly>
                                  </div>
                                </div>
                              </div> -->

                  <div class="row g-4 m-0 mb-5 budget-item-wrapper justify-content-center step-3 align-items-stretch">
                    <!-- Left side-->
                    <div class="col-md-6">
                      <div class="col content-wrapper section-border h-100 w-100">
                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorized Amount</label>
                          <input type="hidden" v-model="form.qtrAuthLimitAmt">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="isUnlimitedApproval ? 'Unlimited' : ('₦' + formatCurrency(form.qtrAuthLimitAmt || 0).toLocaleString())"
                            placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approved Recurrent Amount</label>
                          <input type="hidden" v-model="form.evalApprovedRecurrentAmount">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + formatCurrency(form.evalApprovedRecurrentAmount || 0).toLocaleString()"
                            placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approved Capital Amount</label>
                          <input type="hidden" v-model="form.evalApprovedCapitalAmount">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + formatCurrency(form.evalApprovedCapitalAmount || 0).toLocaleString()"
                            placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Approval Balance</label>
                          <input type="hidden" v-model="form.evalApprovalBalance">
                          <input type="text" class="form-control view-only-input text-end"
                            :value="'₦' + formatCurrency(isUnlimitedApproval ? totalApprovedInQtr : (form.evalApprovalBalance || 0)).toLocaleString()"
                            placeholder="" readonly>
                        </div>
                      </div>
                    </div>

                    <!-- Right side-->
                    <div class="col-md-6">
                      <div class="col content-wrapper section-border h-100 w-100">
                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorization Limit
                            Number</label>
                          <input type="text" class="form-control view-only-input text-end"
                            :value="isUnlimitedApproval ? 'Unlimited' : form.noOfAuthLeftInQtr" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Number Already Approved In
                            Quarter</label>
                          <input type="text" class="form-control view-only-input text-end"
                            v-model="form.noOfAppLimitPerQtr" placeholder="" readonly>
                        </div>

                        <div class="content d-flex">
                          <label for="year" class="form-label fw-bold fixed-label">Quarterly Authorized Limit
                            Balance</label>
                          <input type="text" class="form-control view-only-input text-end"
                            :value="isUnlimitedApproval ? 'Unlimited' : form.noOfAuthLeftInQtr" placeholder="" readonly>
                        </div>

                      </div>
                    </div>


                    <div class="col-md-12 row form-group position-relative floating-label-group mt-3">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Evaluation Remark</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <textarea type="text" class="form-control form-view-only-input" v-model="form.evalRemarks"
                            placeholder="" rows="3" readonly>
                          </textarea>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Total Amount Recommended</label></div>
                      <div class="col-md-9">
                        <div class="col-md-12 input-right">
                          <input type="text" class="form-control form-view-only-input"
                            :value="`₦${formatCurrency(totalRecommended || 0).toLocaleString()}`" placeholder=""
                            readonly>
                        </div>
                      </div>
                    </div>

                    <div v-if="(form.sectoralHeadRemarks && form.sectoralHeadRemarks.trim())"
                      class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Sectoral Head Remark</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <input type="text" class="form-control form-view-only-input"
                            v-model="form.sectoralHeadRemarks" placeholder="" readonly>
                        </div>
                      </div>
                    </div>

                    <div v-if="(form.dobRemarks && form.dobRemarks.trim())"
                      class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Director of Budget Remark</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <input type="text" class="form-control form-view-only-input" v-model="form.dobRemarks"
                            placeholder="" readonly>
                        </div>
                      </div>
                    </div>

                    <div v-if="(form.permSecRemarks && form.permSecRemarks.trim())"
                      class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Permanent Secretary Remark</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <input type="text" class="form-control form-view-only-input" v-model="form.permSecRemarks"
                            placeholder="" readonly>
                        </div>
                      </div>
                    </div>

                    <div v-if="(form.permSecRemarks && form.permSecRemarks.trim())"
                      class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                        <label class="form-label">PS Recommendation</label>
                      </div>
                      <div class="col-md-9 input-right d-flex align-items-center gap-3">
                        <input type="text" class="form-control form-view-only-input" :value="recommendationText"
                          placeholder="" readonly>
                      </div>
                    </div>

                    <div v-if="(form.permSecRemarks && form.permSecRemarks.trim())" :class="['row form-group position-relative floating-label-group',
                      hasSavedCommissionerRemark ? 'col-md-12' : 'col-md-12 content-wrapper section-border']">
                      <div :class="['label-left d-flex justify-content-end align-items-center',
                        hasSavedCommissionerRemark ? 'col-md-3' : 'col-md-3']"><label for="year"
                          class="form-label">Commissioner Remark</label>
                      </div>
                      <div :class="['input-right',
                        hasSavedCommissionerRemark ? 'col-md-9' : 'col-md-9']">
                        <div class="col-md-12 input-right">
                          <textarea v-if="canEdit" class="form-control" id="description"
                            v-model="form.commissionerRemarks" rows="3" maxlength="200" required></textarea>
                          <small v-if="canEdit" class="form-text text-muted char-counter"
                            :title="`${remainingChars(form.commissionerRemarks)} characters remaining`">
                            {{ (form.commissionerRemarks || '').length }}/200 characters
                          </small>

                          <!-- once saved, switch to readonly input -->
                          <input v-else type="text" class="form-control form-view-only-input"
                            v-model="form.commissionerRemarks" readonly />
                        </div>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group"
                      v-if="!canEdit">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                          class="form-label">Appraisal Status</label></div>
                      <div class="col-md-9 input-right">
                        <div class="col-md-12 input-right">
                          <input type="text" class="form-control form-view-only-input" v-model="appraisalStatus"
                            placeholder="" readonly>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4">
                <div v-if="step === 1" class="d-flex justify-content-end">
                  <button type="button" class="btn btn-primary next-btn d-inline-flex align-items-center"
                    @click="handleNext" :disabled="loading" :aria-label="loading ? 'Loading…' : 'Next step'"
                    aria-live="polite">
                    <!-- Spinner appears only when loading = true -->
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>

                    <!-- Button text -->
                    <span class="me-2">{{ loading ? 'Loading...' : 'Next' }}</span>

                    <!-- Iconify arrow icon -->
                    <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                  </button>
                </div>

                <div v-else-if="step < totalSteps" class="d-flex justify-content-between">
                  <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                    <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                    Previous
                  </button>
                  <button type="button" class="btn btn-outline-primary next-btn" @click="handleNext">
                    <span>Next</span>
                    <span class="iconify" data-icon="formkit:arrowright" style="font-size: 1.2em;"></span>
                  </button>
                </div>

                <div v-else-if="step === totalSteps" class="d-flex justify-content-between">
                  <div class="back-btn">
                    <button type="button" class="btn btn-secondary back-btn" @click="handleBack">
                      <span class="iconify" data-icon="formkit:arrowleft" style="font-size: 1.2em;"></span>
                      Previous
                    </button>
                  </div>

                  <div class="next-btn" v-if="form.sectoralHeadRemarks && form.dobRemarks && form.permSecRemarks">
                    <button v-if="canEdit" type="submit" class="btn btn-success accept-btn me-2"
                      @click="handleApprove" :disabled="loadingAccept || submitted">
                      <span v-if="loadingAccept" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ loadingAccept ? 'Processing...' : 'Accept' }}
                    </button>

                    <button v-if="canEdit" type="button" class="btn btn-warning return-btn  me-2"
                      @click="showReturnModal" :disabled="loadingReturn || submitted">
                      <span v-if="loadingReturn" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ loadingReturn ? 'Processing...' : 'Return' }}
                    </button>


                    <button v-if="canEdit" type="button" class="btn btn-danger reject-btn me-2"
                      @click="handleReject" :disabled="submitted">
                      Reject
                    </button>


                    <!-- <button v-if="appraisalStatus === 'Completed'" type="button"
                      class="btn btn-outline-primary download-btn-app" @click="downloadWarrant"
                      :disabled="loadingDownload" aria-live="polite"
                      :aria-label="loadingDownload ? 'Downloading...' : 'Download Warrant'">
                      <span v-if="loadingDownload" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ loadingDownload ? 'Downloading...' : 'Download Warrant' }}
                    </button> -->
                  </div>
                </div>

              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showMemoPreview" class="memo-preview-overlay" @click="closeMemoPreview">
      <div class="memo-preview-modal" @click.stop>
        <div class="memo-preview-header">
          <h5 class="mb-0">Memo Details</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeMemoPreview"></button>
        </div>

        <div class="memo-preview-body">
          <div v-if="loadingMemoPreview" class="preview-grid mb-3">
            <div class="preview-item" v-for="n in 5" :key="n">
              <small>&nbsp;</small>
              <p class="mb-0 memo-skeleton-bar"></p>
            </div>
          </div>

          <p v-else-if="memoPreviewError" class="text-danger mb-0">{{ memoPreviewError }}</p>

          <div v-else class="preview-grid mb-3">
            <div class="preview-item">
              <small>File Title</small>
              <p class="mb-0">{{ memoPreviewData?.fileTitle || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>File Number</small>
              <p class="mb-0">{{ memoPreviewData?.fileNumber || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Originating MDA</small>
              <p class="mb-0">{{ memoPreviewData?.originatingMda || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Amount Approved</small>
              <p class="mb-0">₦{{ formatMemoAmount(memoPreviewData?.amountApproved || 0) }}</p>
            </div>
            <div class="preview-item">
              <small>Created Date</small>
              <p class="mb-0">{{ formatMemoDate(memoPreviewData?.createdDate) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import DashboardLayout from '../../../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useRoute } from 'vue-router';
import { bus } from '@/eventBus'
import { useRouter } from 'vue-router';
import { promptViewPendingAppraisals } from '@/utils/appraisalRedirectPrompt';

const router = useRouter();

const fromNotifications = computed(() => route.query.from === 'ViewPendingNotifications' && route.query.entityType && route.query.statusInd)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const totalSteps = 3
const step = ref(1)
const loading = ref(false)

// Progress bar and step
const steps = [
  { label: 'General Information' },
  { label: 'Budget' },
  { label: 'Evaluation Of Expenditure Approval Limits' }
]

const progressWidth = computed(() => {
  const total = steps.length;
  const percentPerStep = 100 / total; // each step adds this much
  return (step.value * percentPerStep) + '%';
});

const route = useRoute()
const id = route.params.id
const appraisalGroupTypeId = ref('')
const loadingAccept = ref(false)
const submitted = ref(false)
const hasSavedCommissionerRemark = ref(false)
// const loadingDownload = ref(false)
const isApproved = ref(false)
const isRejected = ref(false)
const isReturned = ref(false)
const isRecalled = ref(false)
const isUnlimitedApproval = ref(false)
const canEdit = computed(() => !hasSavedCommissionerRemark.value && !isRecalled.value && !isReturned.value)

// Reactive form data
const form = reactive({
  id: '',
  originatingMda: '',
  approvalAuthority: '',
  natureOfExpenditure: '',
  amountApproved: '',
  purposeOfRequest: '',
  approvedBudgetAmount: '',
  amtReleasedToDate: '',
  balance: '',
  performance: '',
  quarter: '',
  budgetYear: '',
  appraisalInitiatedDate: '',
  noOfAppLimitPerQtr: '',
  noOfAuthIssuedInQtr: '',
  noOfAuthLeftInQtr: '',
  qtrAuthLimitAmt: '',
  approvedRecurrentAmount: '',
  approvedCapitalAmount: '',
  approvalBalance: '',
  evalRemarks: '',
  amtRecommended: '',
  sectoralHeadRemarks: '',
  dobRemarks: '',
  commissionerRemarks: '',
  orgCode: '',
  permSecRemarks: '',
  appraisalInitiatorId: '',
  sectoralHeadId: '',
  sectoralHeadRemarkDate: '',
  dobId: '',
  dobRemarkDate: '',
  permSecId: '',
  permSecRemarksDate: '',
  commissionerId: '',
  commRemarkDate: '',
  issueDate: '',
  approvalAuthorityId: '',
  mdaId: '',
  evalApprovedRecurrentAmount: '',
  evalApprovedCapitalAmount: '',
  evalApprovalBalance: '',
  permSecRecommendedInd: '',
  groupTypeId: '',
  memoFileNumber: '',
  memoId: '',
  pvMemoId: '',
  payVoucherId: '',
  fileTrackerNumber: '',
})

const economicItems = ref([])

// const appraisalOfficers = ref([])

const memo = reactive({
  id: '',
  pvMemoId: '',
  payVoucherId: '',
})

const hasAssociatedPaymentVoucher = computed(() => {
  return (memo.pvMemoId !== null && memo.pvMemoId !== undefined && String(memo.pvMemoId).trim() !== '') ||
    (memo.payVoucherId !== null && memo.payVoucherId !== undefined && String(memo.payVoucherId).trim() !== '')
})

const showMemoPreview = ref(false)
const memoPreviewData = ref(null)
const loadingMemoPreview = ref(false)
const memoPreviewError = ref('')

const formatMemoAmount = (val) => {
  const num = parseFloat(val || 0)
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatMemoDate = (dateValue) => {
  if (!dateValue) return 'N/A'
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const openMemoPreview = async () => {
  const fileNumber = form.memoFileNumber
  if (!fileNumber) return

  showMemoPreview.value = true
  loadingMemoPreview.value = true
  memoPreviewError.value = ''
  memoPreviewData.value = null

  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.post(`${root_api_url}/appraisal/memo/number`, { memoFileNumber: fileNumber }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (data?.errorMessage) {
      throw new Error(data.errorMessage)
    }

    memoPreviewData.value = data
  } catch (error) {
    console.error('Error fetching memo details:', error)
    memoPreviewError.value = error?.response?.data?.errorMessage || error?.response?.data?.error || error?.response?.data?.message || 'Unable to load memo details. Please try again.'
  } finally {
    loadingMemoPreview.value = false
  }
}

const closeMemoPreview = () => {
  showMemoPreview.value = false
}

const fetchMemoById = async (memoId) => {
  const token = Cookies.get('auth_token')
  const { data } = await axios.get(`${root_api_url}/appraisal/memo/${memoId}`, {
    headers: { Authorization: `Bearer ${token}` }
  })

  memo.id = data.id
  memo.pvMemoId = data.pvMemoId
  memo.payVoucherId = data.payVoucherId
  form.pvMemoId = data.pvMemoId ?? ''
  form.payVoucherId = data.payVoucherId ?? ''
}

const fetchMemoForAppraisal = async (appraisalData) => {
  try {
    const directMemoId = appraisalData.memoId || appraisalData.memo?.id || appraisalData.memoDto?.id
    if (directMemoId) {
      await fetchMemoById(directMemoId)
      form.memoId = directMemoId
      return
    }

    if (!appraisalData.memoFileNumber) return

    const token = Cookies.get('auth_token')
    const { data } = await axios.get(`${root_api_url}/appraisal/memo/all`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    const matchedMemo = (data || []).find((item) => item.fileNumber === appraisalData.memoFileNumber)
    if (matchedMemo?.id) {
      form.memoId = matchedMemo.id
      await fetchMemoById(matchedMemo.id)
    }
  } catch (err) {
    console.error('Error resolving memo payment voucher details:', err)
  }
}

const fetchApprovalAuthorityDetails = async (approvalAuthorityId) => {
  if (!approvalAuthorityId) return
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/eli/app-auth/${approvalAuthorityId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    isUnlimitedApproval.value = data.unlimitedApprovalInd === 1
  } catch (err) {
    console.error('Error fetching approval authority details:', err)
  }
}

const fetchAppraisal = async () => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/appraisal/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    // console.log(data);
    form.originatingMda = data.originatingMda;
    form.approvalAuthority = data.approvalAuthority;
    form.natureOfExpenditure = data.natureOfExpenditure

    form.amountApproved = data.amountApproved;
    form.purposeOfRequest = data.purposeOfRequest;
    form.approvedBudgetAmount = data.approvedBudgetAmount;
    form.amtReleasedToDate = data.amtReleasedToDate;
    form.balance = data.balance;
    form.performance = data.performance;
    form.quarter = data.quarter;
    form.budgetYear = data.budgetYear;
    form.appraisalInitiatedDate = data.appraisalInitiatedDate;
    form.noOfAppLimitPerQtr = data.noOfAppLimitPerQtr;
    form.noOfAuthIssuedInQtr = data.noOfAuthIssuedInQtr;
    form.noOfAuthLeftInQtr = data.noOfAuthLeftInQtr;
    form.qtrAuthLimitAmt = data.qtrAuthLimitAmt;
    form.approvedRecurrentAmount = data.approvedRecurrentAmount;
    form.approvedCapitalAmount = data.approvedCapitalAmount;
    form.approvalBalance = data.approvalBalance;
    form.evalRemarks = data.evalRemarks;
    form.orgCode = data.orgCode;
    form.sectoralHeadRemarks = data.sectoralHeadRemarks;
    form.amtRecommended = data.amtRecommended;
    form.dobRemarks = data.dobRemarks;
    form.permSecRemarks = data.permSecRemarks;
    form.appraisalInitiatorId = data.appraisalInitiatorId;
    form.sectoralHeadId = data.sectoralHeadId;
    form.sectoralHeadRemarkDate = data.sectoralHeadRemarkDate;
    form.dobId = data.dobId;
    form.dobRemarkDate = data.dobRemarkDate;
    form.permSecId = data.permSecId;
    form.permSecRemarksDate = data.permSecRemarksDate;
    form.issueDate = data.issueDate;
    form.approvalAuthorityId = data.approvalAuthorityId;
    await fetchApprovalAuthorityDetails(data.approvalAuthorityId);
    form.mdaId = data.mdaId;
    form.permSecRecommendedInd = data.permSecRecommendedInd;
    if (data.commissionerRemarks) {
      form.commissionerRemarks = data.commissionerRemarks;
      form.commissionerId = data.commissionerId;
      form.commRemarkDate = data.commRemarkDate;
    }

    hasSavedCommissionerRemark.value = !!data.commissionerRemarks;

    isApproved.value = Boolean(data.warrantApprovedInd);
    isRejected.value = Boolean(data.appraisalRejectedInd);
    isRecalled.value = Boolean(data.recalledInd);

    form.evalApprovedRecurrentAmount = data.evalApprovedRecurrentAmount;
    form.evalApprovedCapitalAmount = data.evalApprovedCapitalAmount;
    form.evalApprovalBalance = data.evalApprovalBalance;
    form.groupTypeId = data.groupTypeId;
    appraisalGroupTypeId.value = data.groupTypeId;
    form.memoFileNumber = data.memoFileNumber;
    form.fileTrackerNumber = data.fileTrackerNumber;
    form.memoId = data.memoId || '';

    await fetchMemoForAppraisal(data)

    economicItems.value = (data.appraisalEconomicItems || []).map(dto => ({
      id: dto.id,
      //ecoCode: dto.economicLineItemDto.economicCode,
      ecoCode: dto.ecoCode,
      title: dto.title,
      mdaLineItemBudgetId: dto.mdaLineItemBudgetId,
      amountBudgeted: dto.amountBudgeted,
      amountReleased: dto.amountReleased,
      balance: dto.balance,
      amountRequired: dto.amountRequired,
      year: dto.year,
      quarter: dto.quarter,
      eliAmountRecommended: dto.amountRecommended || 0
    }));

    if (isApproved.value) {
      step.value = 3
    }

  } catch (err) {
    console.error('Error loading ECO items:', err)
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load appraisal details. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  }
}

// const fetchAppraisalOfficers = async () => {
//   const token = Cookies.get('auth_token')
//   try {
//     const {data} = await axios.get(`${root_api_url}/appraisal/appraisal-officers/${appraisalGroupTypeId.value}`, {
//       headers: { Authorization: `Bearer ${token}` }
//     })
//     appraisalOfficers.value = data.map(officer => ({
//       id: officer.id,
//       roleName: officer.roleName,
//     }))

//   } catch (err) {
//     console.error('Error fetching appraisal officers:', err)
//     const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
//     Swal.fire({
//       html: `${iconHtmlError}
//       <h1 style="font-weight:600;">Error</h1>
//       <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load appraisal officers. Please try again.'}</p>`,
//       showConfirmButton: true,
//       showCloseButton: true,
//       icon: undefined,
//     });
//   }
// }

const totalRecommended = computed(() => {
  return form.amtRecommended;
});

const totalApprovedInQtr = computed(() =>
  Number(form.evalApprovedRecurrentAmount || 0) + Number(form.evalApprovedCapitalAmount || 0)
)

const natureOfExpenditureLabel = computed(() => {
  return form.natureOfExpenditure == 2
    ? 'Recurrent'
    : form.natureOfExpenditure == 3
      ? 'Capital'
      : form.natureOfExpenditure == 4
        ? 'Hybrid'
        : ''
})

const appraisalStatus = computed(() => {
  // compute the status
  let status

  if (isApproved.value) {
    status = 'Completed'
  } else if (isRecalled.value) {
    status = 'Recalled'
  } else if (isRejected.value) {
    status = 'Rejected'
  } else if (isReturned.value) {
    status = 'Returned'
  } else {
    status = 'Pending'
  }
  // log the raw values and the result
  // console.log(
  //   '🧪 appraisalStatus computed:',
  //   {
  //     dobRemarks: form.dobRemarks,
  //     sectoralHeadRemarks: form.sectoralHeadRemarks,
  //     commissionerRemarks: form.commissionerRemarks,
  //     permSecRemarks: form.permSecRemarks,
  //     status
  //   }
  // );

  return status;
});

const remainingChars = (val) => 200 - (val || '').length

const recommendationText = computed(() => {
  return form.permSecRecommendedInd === '1' || form.permSecRecommendedInd === 1
    ? 'Recommended'
    : 'Not Recommended'
})

function formatCurrency(val) {
  if (val === '' || val === null || val === undefined) return '';
  const n = Number(val);
  if (isNaN(n)) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const formatInitDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'long' })
  const year = date.getFullYear()

  const getOrdinal = (number) => {
    const remainder = number % 100
    if (remainder >= 11 && remainder <= 13) return `${number}th`
    switch (number % 10) {
      case 1:
        return `${number}st`
      case 2:
        return `${number}nd`
      case 3:
        return `${number}rd`
      default:
        return `${number}th`
    }
  }

  return `${getOrdinal(day)} ${month} ${year}`
}




const handleSubmit = async () => {
}

const handleApprove = async () => {
  const token = Cookies.get('auth_token');

  loadingAccept.value = true;
  submitted.value = true;

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  if (!isUnlimitedApproval.value && !(Number.parseInt(form.noOfAuthLeftInQtr, 10) >= 0)) {
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Approval Authority has exceeded limit balance</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });

    loadingAccept.value = false;
    submitted.value = false;

    return;
  }

  if (!isUnlimitedApproval.value && !(totalRecommended.value <= Number.parseInt(form.approvalBalance, 10))) {
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Approval Authority has exceeded limit amount balance</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });

    loadingAccept.value = false;
    submitted.value = false;

    return;
  }

  const appraisalData = {
    ...form,
    commissionerRemarks: form.commissionerRemarks,
    natureOfExpenditure: form.natureOfExpenditure,
    id: id,
    pvMemoId: form.pvMemoId ?? null,
    payVoucherId: form.payVoucherId ?? null,
    appraisalEconomicItems: economicItems.value.map(item => ({
      id: item.id,
      appraisalId: id,
      mdaLineItemBudgetId: item.mdaLineItemBudgetId,
      ecoCode: item.ecoCode,
      title: item.title,
      amountBudgeted: item.amountBudgeted,
      amountReleased: item.amountReleased,
      balance: item.balance,
      amountRequired: item.amountRequired,
      amountRecommended: item.eliAmountRecommended,
      year: item.year,
      quarter: item.quarter
    }))
  }

  try {
    const { data } = await axios.put(`${root_api_url}/appraisal/appraisal/${id}`, appraisalData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    await Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Appraisal Approved Successfully</p> `,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    }).then(() => {
      hasSavedCommissionerRemark.value = true
      bus.emit('refresh-notifications')
      isApproved.value = Boolean(data.warrantApprovedInd);
      form.commissionerId = data.commissionerId;
      form.commRemarkDate = data.commRemarkDate;
      form.issueDate = data.issueDate;
      // window.location.reload()
    });

    await promptViewPendingAppraisals(router, form.mdaId, form.originatingMda);

  } catch (error) {
    // Swal.fire({
    //   html: `${iconHtmlError}
    //   <h1 style="font-weight:600;">Error</h1>
    //   <p>Failed to Approve Appraisal</p>`,
    //   showConfirmButton: true,
    //   showCloseButton: true,
    //   icon: undefined,
    // });

    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loadingAccept.value = false;
    submitted.value = false;
  }
}

const handleReject = async () => {
  const token = Cookies.get('auth_token');

  loadingAccept.value = true;
  submitted.value = true;

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const appraisalData = {
    ...form,
    commissionerRemarks: form.commissionerRemarks,
    natureOfExpenditure: form.natureOfExpenditure,
    id: id,
    pvMemoId: form.pvMemoId ?? null,
    payVoucherId: form.payVoucherId ?? null,
    appraisalRejectedInd: 1,
    appraisalEconomicItems: economicItems.value.map(item => ({
      id: item.id,
      appraisalId: id,
      mdaLineItemBudgetId: item.mdaLineItemBudgetId,
      ecoCode: item.ecoCode,
      title: item.title,
      amountBudgeted: item.amountBudgeted,
      amountReleased: item.amountReleased,
      balance: item.balance,
      amountRequired: item.amountRequired,
      amountRecommended: item.eliAmountRecommended,
      year: item.year,
      quarter: item.quarter
    }))
  }

  try {
    const { data } = await axios.put(`${root_api_url}/appraisal/appraisal/${id}`, appraisalData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    await Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Appraisal Rejected Successfully</p> `,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    }).then(() => {
      hasSavedCommissionerRemark.value = true
      bus.emit('refresh-notifications')
      isRejected.value = Boolean(data.appraisalRejectedInd);
    });

    await promptViewPendingAppraisals(router, form.mdaId, form.originatingMda);

  } catch (error) {
    // Swal.fire({
    //   html: `${iconHtmlError}
    //   <h1 style="font-weight:600;">Error</h1>
    //   <p>Failed to Reject Appraisal</p>`,
    //   showConfirmButton: true,
    //   showCloseButton: true,
    //   icon: undefined,
    // });

    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loadingAccept.value = false;
    submitted.value = false;
  }
}

/* Return Selector
  <div class="form-group">
    <label for="receiverSelect" class="form-label">Receiver</label>
    <select id="receiverSelect" v-model="receiver" class="form-select" required>
      <option value="">- Select Receiver -</option>
      <option v-for="officer in appraisalOfficers" :key="officer.id" :value="officer.id">
        {{ officer.roleName }}
      </option>
    </select>
  </div>
*/

const showReturnModal = async () => {
  form.commissionerRemarks = ''
  await Swal.fire({
    title: 'Return Appraisal',
    html: `
        <div class="form-group">
          <label for="returnRemarks" class="form-label">Remarks <span class="text-warning">*</span></label>
          <textarea id="returnRemarks" v-model="form.commissionerRemarks" class="form-control" rows="4" maxlength="200"
            placeholder="Please provide your remarks for returning this appraisal..." required></textarea>
          <small id="returnRemarksCounter" class="text-muted char-counter" style="display:block;margin-top:0.25rem;">0/200 characters</small>
        </div>
        <div class="modal-footer">
          <button type="button" id="returnAppraisalButton" class="btn btn-warning">
            Return Appraisal
          </button>
        </div>`,
    showCancelButton: false,
    showConfirmButton: false,
    showCloseButton: true,
    customClass: {
      popup: 'ogpabs-swal',
      htmlContainer: 'ogpabs-swal-html'
    },
    didOpen: () => {
      const textarea = document.getElementById('returnRemarks')
      const counter = document.getElementById('returnRemarksCounter')
      const updateCounter = () => {
        counter.textContent = `${textarea.value.length}/200 characters`
      }
      updateCounter()
      textarea.addEventListener('input', updateCounter)

      const btn = document.getElementById('returnAppraisalButton')
      btn.addEventListener('click', async () => {
        const remarks = document.getElementById('returnRemarks').value.trim()

        if (!remarks) {
          Swal.showValidationMessage('Remarks cannot be empty')
          return
        }

        form.commissionerRemarks = remarks
        await handleReturn()
      })
    },
  })
}

const handleReturn = async () => {
  const token = Cookies.get('auth_token');

  loadingAccept.value = true;
  submitted.value = true;

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const appraisalData = {
    ...form,
    commissionerRemarks: form.commissionerRemarks,
    natureOfExpenditure: form.natureOfExpenditure,
    id: id,
    pvMemoId: form.pvMemoId ?? null,
    payVoucherId: form.payVoucherId ?? null,
    returnedAppraisalInd: 1,
    appraisalEconomicItems: economicItems.value.map(item => ({
      id: item.id,
      appraisalId: id,
      mdaLineItemBudgetId: item.mdaLineItemBudgetId,
      ecoCode: item.ecoCode,
      title: item.title,
      amountBudgeted: item.amountBudgeted,
      amountReleased: item.amountReleased,
      balance: item.balance,
      amountRequired: item.amountRequired,
      amountRecommended: item.eliAmountRecommended,
      year: item.year,
      quarter: item.quarter
    }))
  }

  try {
    const { data } = await axios.put(`${root_api_url}/appraisal/appraisal/${id}`, appraisalData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    await Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Appraisal Returned Successfully</p> `,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    }).then(() => {
      hasSavedCommissionerRemark.value = true
      bus.emit('refresh-notifications')
      isReturned.value = Boolean(data.returnedAppraisalInd);
    });

    await promptViewPendingAppraisals(router, form.mdaId, form.originatingMda);

  } catch (error) {
    // Swal.fire({
    //   html: `${iconHtmlError}
    //   <h1 style="font-weight:600;">Error</h1>
    //   <p>Failed to Return Appraisal</p>`,
    //   showConfirmButton: true,
    //   showCloseButton: true,
    //   icon: undefined,
    // });

    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loadingAccept.value = false;
    submitted.value = false;
  }
}


onMounted(() => {
  fetchAppraisal(),
  document.title = "Update Appraisal"
})



const handleNext = async () => {
  // if (step.value === 2 && !hasSavedCommissionerRemark.value) {
  //   const missing = economicItems.value.some(item => !item.eliAmountRecommended)

  //   if (missing) {
  //     // Show error if any recommended‑amount is blank/zero
  //     await Swal.fire({
  //       icon: 'warning',
  //       title: 'Incomplete',
  //       text: 'Please enter an Amount Recommended for every line item before proceeding.',
  //     })
  //     return
  //   }

  //   form.amtRecommended = totalRecommended.value;
  // }

  if (step.value < totalSteps) step.value++
}

const handleBack = () => {
  if (step.value > 1) step.value--
}


// const downloadWarrant = async () => {
//   loadingDownload.value = true
//   const token = Cookies.get('auth_token')


//   const payload = {
//     ...form,
//     amtRecommended: form.amtRecommended,
//     sectoralHeadRemarks: form.sectoralHeadRemarks,
//     natureOfExpenditure: form.natureOfExpenditure,
//     id: id,
//     appraisalEconomicItems: economicItems.value.map(item => ({
//       id: item.id,
//       appraisalId: id,
//       mdaLineItemBudgetId: item.mdaLineItemBudgetId,
//       ecoCode: item.ecoCode,
//       title: item.title,
//       amountBudgeted: item.amountBudgeted,
//       amountReleased: item.amountReleased,
//       balance: item.balance,
//       amountRequired: item.amountRequired,
//       amountRecommended: item.eliAmountRecommended,
//       year: item.year,
//       quarter: item.quarter
//     }))
//   }

//   try {
//     const response = await axios.post(
//       `${root_api_url}/appraisal/warrant/pdf`,   // <-- your download endpoint
//       payload,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           'Content-Type': 'application/json'
//         },
//         responseType: 'blob'     // important for binary/pdf
//       }
//     )

//     // trigger file download
//     const blob = new Blob([response.data], { type: response.data.type })
//     const fileName = `warrant_${form.id}.pdf`
//     if (window.navigator && window.navigator.msSaveOrOpenBlob) {
//       // IE11
//       window.navigator.msSaveOrOpenBlob(blob, fileName)
//     } else {
//       const url = window.URL.createObjectURL(blob)
//       const link = document.createElement('a')
//       link.href = url
//       link.setAttribute('download', fileName)
//       document.body.appendChild(link)
//       link.click()
//       document.body.removeChild(link)
//       window.URL.revokeObjectURL(url)
//     }
//   } catch (err) {
//     console.error('Download failed:', err)
//     // optionally: show a toast to the user
//   } finally {
//     loadingDownload.value = false
//   }
// }

</script>

<style scoped>
.marquee-wrapper {
  width: 100%;
  overflow: hidden;
  background-color: #f2ffe6;
  padding: 0.5rem 0;
}

.marquee-content {
  display: inline-flex;
  white-space: nowrap;
  animation: marquee 20s linear infinite;
}

.marquee-content p {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0 2rem;
  font-size: 1rem;
  font-weight: 500;
  color: #856404;
}

.marquee-content:hover {
  animation-play-state: paused;
}

@keyframes marquee {
  0% {
    transform: translateX(100%);
  }

  100% {
    transform: translateX(-100%);
  }
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
}

.budget-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  /* forces every .col to match the tallest */
}

.budget-item-wrapper .view-only-input,
.budget-item-wrapper input.view-only-input {
  flex: 1;
  /* inputs (and your Approval‐Authority <div>) all grow equally */
  /* for your Approval Authority div: */
  white-space: pre-wrap;
}

.dashboard-layout .budget-item-wrapper {
  margin-top: 20px;
  padding: 0 40px;
}

.dashboard-layout .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.dashboard-layout .section-border {
  /* padding: 1rem; */
  border-top: 3px solid #667eea !important;
}

.dashboard-layout .step-2 {
  padding: 0 20px;
}

.dashboard-layout .budget-item-wrapper.step-2 .col {
  border-right: 1px solid var(--border);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
}

.dashboard-layout .budget-item-wrapper.step-2 .col.no-border {
  border-right: none !important;
}

.dashboard-layout .budget-item-wrapper.step-3 .fixed-label {
  min-height: 80px;
  justify-content: flex-start;
  width: 350px;
  box-sizing: border-box;
  text-align: left;
  /* all: unset; */
  display: flex;
  margin-bottom: 0;
  flex-shrink: 0;
  margin-left: 1rem;
}

.dashboard-layout .budget-item-wrapper.step-3 .col {
  padding: 0;
}

.dashboard-layout .budget-item-wrapper.step-3 .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* padding: 1rem; */
}

.dashboard-layout .budget-item-wrapper.step-3 .content {
  border-bottom: 2px solid var(--border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.dashboard-layout .budget-item-wrapper.step-3 label {
  font-size: 16px;
  padding: .375rem 0 !important;
  margin-bottom: 0px !important;
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-3.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border {
  border: none;
}

.dashboard-layout .budget-item-wrapper .col-md-2.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .fixed-label {
  display: flex;
  align-items: center;
  /* vertical center */
  justify-content: center;
  /* horizontal center */
  min-height: 4rem;
  width: 100%;
  padding: 0 0.25rem;
  box-sizing: border-box;
  text-align: center;
}

.memo-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.memo-preview-modal {
  width: min(920px, 100%);
  max-height: 90vh;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.memo-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.memo-preview-body {
  padding: 1rem;
  overflow: auto;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.preview-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.preview-item small {
  color: #6b7280;
  font-size: 0.75rem;
}

.preview-item p {
  color: var(--color);
  font-weight: 600;
}

@keyframes memoSkeletonShimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

.memo-skeleton-bar {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f6f7 37%, #eceff1 63%);
  background-size: 400% 100%;
  animation: memoSkeletonShimmer 1.4s ease infinite;
}

@media (max-width: 576px) {
  .preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
